import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      // Try with stored token
      const token = localStorage.getItem("appmi_token");
      if (token) {
        try {
          const { data } = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(data);
        } catch {
          localStorage.removeItem("appmi_token");
          setUser(false);
        }
      } else {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.hash?.includes('session_token=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      if (data.twofa_required) {
        // Password accepted but a second factor is required — no session yet.
        return { success: false, twofa_required: true, challenge_token: data.challenge_token };
      }
      if (data.token) localStorage.setItem("appmi_token", data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  // Second step of a 2FA login: exchange the challenge token + TOTP/recovery
  // code for a real session. Mirrors login()'s token storage + user state.
  const completeTwoFactor = async (challengeToken, code) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/2fa/challenge`,
        { challenge_token: challengeToken, code },
        { withCredentials: true }
      );
      if (data.token) localStorage.setItem("appmi_token", data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return {
        success: false,
        status: e.response?.status,
        error: formatApiError(e.response?.data?.detail) || e.message,
      };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
      if (data.token) localStorage.setItem("appmi_token", data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    localStorage.removeItem("appmi_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, completeTwoFactor, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
